import type { FC, ReactNode } from 'react'

import { useEffect } from 'react'
import { useRouter } from 'next/router'

import { GTM_CONTAINER_ID } from '@/constants/analytics'

import {
    applyConsentToGoogle,
    clearGoogleCookies,
    readConsent,
    subscribeToConsent
} from '@/lib/consent'

const GTM_SCRIPT_ID = 'gtm-container'

/**
 * Injects the GTM container. Deliberately not rendered server-side: the tag must
 * never reach a visitor who has not accepted, and consent lives on the device.
 */
const loadGtm = (): void => {
    if (document.getElementById(GTM_SCRIPT_ID)) return

    window.dataLayer = window.dataLayer || []
    window.dataLayer.push({ 'gtm.start': Date.now(), event: 'gtm.js' })

    const script = document.createElement('script')

    script.id = GTM_SCRIPT_ID
    script.async = true
    script.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_CONTAINER_ID}`

    document.head.appendChild(script)
}

export const Analytics: FC = (): ReactNode => {
    const router = useRouter()

    useEffect(() => {
        if (readConsent()?.choice === 'granted') {
            applyConsentToGoogle('granted')
            loadGtm()
        }

        return subscribeToConsent(choice => {
            applyConsentToGoogle(choice)

            if (choice === 'granted') {
                loadGtm()
                return
            }

            clearGoogleCookies()
        })
    }, [])

    useEffect(() => {
        // GTM's built-in page view trigger only fires on a full document load, so
        // client-side route changes need an explicit push to be counted.
        const handleRouteChange = (url: string): void => {
            if (readConsent()?.choice !== 'granted') return

            window.dataLayer = window.dataLayer || []
            window.dataLayer.push({
                event: 'pageview',
                page_path: url,
                page_title: document.title
            })
        }

        router.events.on('routeChangeComplete', handleRouteChange)

        return () => router.events.off('routeChangeComplete', handleRouteChange)
    }, [router.events])

    return null
}
